"use client";

/**
 * Single profile card in the recipient list. Clean cards follow Figma
 * `1678:1005`; cards with blocking issues follow `1681:1860`:
 *
 *   - Clean: soft `white/2` card, hairline `white/5` border; right gutter
 *     holds a green-tinted status mini-button (check) + a neutral trash
 *     mini-button.
 *   - Fix: red `rgba(237,59,59,0.25)` border, top section washed with a
 *     subtle red-tinted vertical gradient, red status mini-button (X),
 *     and a solid `#ed3b3b` footer strip — "{n} Issue(s) found" in white.
 *   - Warning: same structure in the amber family ("Will start
 *     staggered" footer).
 *
 * Composition (left→right): 56px `CandidateAvatar`; name + source
 * indicators (LinkedIn / email / web); location; company-with-logo; and
 * the "first sends / executes around" stopwatch row (kept deliberately —
 * not part of the Figma frame but load-bearing info). Whole card is
 * clickable (cursor + hover states); selection shows as a brighter
 * border/background without hiding the per-bucket tint.
 */

import * as React from "react";

import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Icon } from "@/components/ui/icon";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { ensureAbsoluteUrl } from "@/lib/url/ensure-absolute-url";
import { logo } from "@/lib/logo-dev";
import { cn } from "@/lib/utils";

import { firstSendDateLabel } from "../../lib/badges";
import { titleCase } from "../../lib/text";
import type {
  EnrollmentPreviewCandidate,
  PreviewBucket,
} from "../../lib/types";

export interface RecipientCardProps {
  candidate: EnrollmentPreviewCandidate;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  /** Sender timezone, threaded down so the badge matches Figma copy. */
  senderTz?: string | null;
}

export function RecipientCard({
  candidate,
  selected,
  onSelect,
  onRemove,
  senderTz,
}: RecipientCardProps) {
  const bucket: PreviewBucket = candidate.bucket ?? "no_issue";
  const blocked = bucket === "fix";
  const warning = bucket === "warning";
  const blockedCount = (candidate.issues ?? []).filter(
    (i) => i.severity === "fix",
  ).length;

  const sendLabel = firstSendDateLabel(candidate.first_step_due_at, senderTz);

  const companyDomain = candidate.current_company_domain ?? null;
  const companyName = titleCase(candidate.current_company) || null;
  const locationLabel = titleCase(candidate.location) || null;
  const companyLogoUrl = logo({
    domain: companyDomain,
    name: companyName ?? undefined,
    size: 36,
    format: "png",
  });

  // We don't list every social link — just the surfaces we actually have
  // contact data on, as live links (LinkedIn opens in a new tab, the @
  // icon is a mailto). No globe indicator: the preview payload carries no
  // website URL, so there'd be nothing to open.
  const linkedinUrl = candidate.resolved_linkedin_url
    ? ensureAbsoluteUrl(candidate.resolved_linkedin_url)
    : null;
  const email = candidate.resolved_email ?? null;

  // 24px status/action mini-buttons (Figma `1681:1883` / `1678:1019`):
  // tinted hairline border + 4%-tint fill for the status, neutral for trash.
  const MINI_BTN =
    "flex size-6 shrink-0 items-center justify-center rounded-[5px] border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]";

  return (
    <li>
      {/* Use a div+role=button for the row so the nested Remove control
          can be a real <button> (HTML disallows nested <button>s). */}
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
        aria-pressed={selected}
        aria-label={`Select ${candidate.full_name}`}
        className={cn(
          "group flex w-full cursor-pointer flex-col overflow-clip rounded-lg border text-left transition-colors",
          // Border: per-bucket tint (Figma); selection brightens it so the
          // active card stays unambiguous without losing the bucket color.
          blocked
            ? selected
              ? "border-[rgba(237,59,59,0.6)]"
              : "border-[rgba(237,59,59,0.25)] hover:border-[rgba(237,59,59,0.4)]"
            : warning
              ? selected
                ? "border-amber-500/60"
                : "border-amber-500/25 hover:border-amber-500/40"
              : selected
                ? "border-white/25 bg-white/[0.06]"
                : "border-white/5 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/15",
        )}
      >
        {/* Profile info section — red/amber cards get a subtle downward
            tint wash over the base white/2 (Figma `1681:1861`). */}
        <div
          className={cn("flex w-full items-center gap-4 px-3 py-4 max-lg:gap-3", {
            "bg-[linear-gradient(180deg,rgba(249,93,93,0)_0%,rgba(249,93,93,0.05)_100%),linear-gradient(90deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.02)_100%)]":
              blocked,
            "bg-[linear-gradient(180deg,rgba(255,175,83,0)_0%,rgba(255,175,83,0.05)_100%),linear-gradient(90deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.02)_100%)]":
              warning,
          })}
        >
          <CandidateAvatar
            name={candidate.full_name}
            avatarUrl={candidate.avatar_url ?? undefined}
            size={56}
            // ARBI-677: compact card below `lg` — the trailing-! utility
            // overrides the inline 56px width/height from `size`.
            className="max-lg:size-12!"
          />
          <div className="flex flex-1 min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[15px] font-medium text-heading">
                {candidate.full_name}
              </span>
              <div className="flex shrink-0 items-center gap-2.5 text-muted-foreground">
                {linkedinUrl ? (
                  <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${candidate.full_name} on Social`}
                    onClick={(e) => e.stopPropagation()}
                    className="opacity-70 transition-opacity hover:opacity-100"
                  >
                    <Icon name="IconPeopleCircle" size={16} />
                  </a>
                ) : null}
                {email ? (
                  <a
                    href={`mailto:${email}`}
                    aria-label={`Email ${candidate.full_name}`}
                    onClick={(e) => e.stopPropagation()}
                    className="opacity-70 transition-opacity hover:opacity-100"
                  >
                    <Icon name="IconAt" size={16} />
                  </a>
                ) : null}
              </div>
            </div>
            <div className="truncate text-[13px] text-foreground/75">
              {locationLabel ?? "—"}
            </div>
            <div className="flex min-w-0 items-center gap-1.5 text-sm text-foreground">
              {companyLogoUrl ? (
                <IconAvatar
                  src={companyLogoUrl}
                  size={18}
                  alt={companyName ?? ""}
                  className="rounded-sm border border-white/10 bg-surface-header"
                  icon={
                    <Icon name="IconBag" className="text-foreground opacity-40" />
                  }
                />
              ) : null}
              <span className="truncate">{companyName ?? "—"}</span>
            </div>
            {sendLabel ? (
              <div className="flex items-center gap-2 pt-0.5">
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                  <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">
                    <Icon
                      name="IconStopwatch"
                      size={16}
                      className="text-muted-foreground opacity-70"
                    />
                  </span>
                  {sendLabel}
                </span>
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2.5 self-center">
            <span
              className={cn(
                MINI_BTN,
                blocked
                  ? "border-[rgba(249,93,93,0.15)] bg-[rgba(249,93,93,0.04)]"
                  : warning
                    ? "border-[rgba(255,175,83,0.15)] bg-[rgba(255,175,83,0.04)]"
                    : "border-[rgba(102,220,126,0.15)] bg-[rgba(102,220,126,0.04)]",
              )}
              aria-hidden
            >
              {blocked ? (
                <Icon name="IconCircleX" size={16} style={{ color: "#ed3b3b" }} />
              ) : warning ? (
                <Icon
                  name="IconExclamationCircle"
                  size={16}
                  style={{ color: "#ffaf53" }}
                />
              ) : (
                <Icon
                  name="IconCircleCheck"
                  size={16}
                  style={{ color: "#66dc7e" }}
                />
              )}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label={`Remove ${candidate.full_name} from this enrollment`}
              className={cn(
                MINI_BTN,
                "cursor-pointer border-white/10 bg-white/[0.04] text-contrast/60 transition-colors hover:border-destructive/40 hover:text-destructive",
              )}
            >
              <Icon name="IconTrashCanSimple" size={14} />
            </button>
          </div>
        </div>

        {/* Footer strip (Figma `1681:1897`) — solid fill, white copy. */}
        {blocked && blockedCount > 0 ? (
          <div className="w-full border-t border-white/10 bg-[#ed3b3b] px-3 py-2">
            <p className="text-[15px] font-medium text-white">
              {blockedCount} {blockedCount === 1 ? "Issue" : "Issues"} found
            </p>
          </div>
        ) : warning ? (
          <div className="w-full border-t border-white/10 bg-[#b45309] px-3 py-2">
            <p className="text-[15px] font-medium text-white">
              Will start staggered
            </p>
          </div>
        ) : null}
      </div>
    </li>
  );
}
